import React from 'react';
import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Button,
  Table,
  Spinner,
  Text,
} from '@chakra-ui/react';
import qs from 'qs';
import dayjs from 'dayjs';
import ActivityDetail from '../index/ActivityDetail';
import xhr from 'xhr';
import BasicModal from 'components/modal/Modal';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useIndex } from '../index/teacher-context';
import { getTeacherPendingListCutoffIso, getTeacherAiFallbackCutoffIso, } from 'api/profesores';
import { sortSubjectsByPending } from '../sortSubjectsByPending';

var relativeTime = require('dayjs/plugin/relativeTime');
dayjs.extend(relativeTime);

function subjectPendingSittingsDisplay(subject) {
  if (!subject) return 0;
  if (typeof subject.sittingsCount === 'number') return subject.sittingsCount;
  return subject.sittingsCount?.length ?? 0;
}

async function fetchSittingsForSubject(subjectId) {
  const aiFallbackCutoffIso = getTeacherAiFallbackCutoffIso();

  const query = qs.stringify(
    {
      filters: {
        subject: {
          id: { $eq: subjectId },
        },

        // Sin nota, o publicada por la calificación automática y enviada a revisión del docente.
        $and: [
          {
            $or: [
              { score: { $null: true } },
              { review_requested: { $eq: true } },
            ],
          },
          // Nota de una prueba aprobada que espera su hora de publicación: no le toca al docente.
          {
            $or: [
              { publish_approved: { $null: true } },
              { publish_approved: { $eq: false } },
              { score: { $notNull: true } },
            ],
          },
        ],

        createdAt: {
          $gte: getTeacherPendingListCutoffIso(),
        },

        student: {
          schoolFrom: {
            id: { $null: true },
          },
        },

        $or: [
          // Actividad sin IA: aparece normalmente al docente.
          {
            activity: {
              ai: { $eq: false },
            },
          },

          // Por si existen actividades antiguas donde ai sea null.
          {
            activity: {
              ai: { $null: true },
            },
          },

          // Actividad con IA que ya llegó a su corte
          // y sigue sin calificación.
          {
            createdAt: {
              $lte: aiFallbackCutoffIso,
            },
          },

          // La IA no pudo calificarla y la pasó al docente: aparece de inmediato.
          {
            ai_deferred_to_teacher: { $eq: true },
          },

          // Enviada con el flujo anterior (sin revisión previa): no entra a la
          // calificación automática, así que el docente la ve de inmediato.
          {
            precheck_result: { $null: true },
          },

          // Publicada y enviada a revisión desde el panel de calificación.
          {
            review_requested: { $eq: true },
          },
        ],
      },

      pagination: { limit: -1 },

      populate: {
        student: {
          populate: ['user'],
        },

        subject: {
          populate: {
            course: {
              populate: ['program_type'],
            },
            grade: {
              populate: ['program_type'],
            },
            technical_career: {
              populate: ['program_type'],
            },
          },
        },

        activity: true,
        file: true,
        files: true,
      },
    },
    {
      encodeValuesOnly: true,
    }
  );

  const res = await xhr(`/activity-sittings?${query}`);

  const list = res?.data?.data ?? res?.data ?? [];

  return Array.isArray(list) ? list : [];
}

export default function Sittings() {
  const [currentSittingSubjectId, setCurrentSittingSubjectId] = React.useState(null);
  const [sittings, setSittings] = React.useState([]);
  const [currentSitting, setCurrentSitting] = React.useState({});
  const [isListFetching, setIsListFetching] = React.useState(true);
  const [isOpen, setIsOpen] = React.useState(false);
  const [score, setScore] = React.useState();

  const { counts, mutate } = useIndex();

  const activitiesSubjectsSorted = React.useMemo(
    () => sortSubjectsByPending(counts?.activitiesData, subjectPendingSittingsDisplay),
    [counts?.activitiesData]
  );

  const currentSittingSubject = React.useMemo(() => {
    return (
      activitiesSubjectsSorted.find((subject) => subject.id === currentSittingSubjectId) ||
      activitiesSubjectsSorted[0] ||
      null
    );
  }, [activitiesSubjectsSorted, currentSittingSubjectId]);

  React.useEffect(() => {
    if (!activitiesSubjectsSorted.length) {
      setCurrentSittingSubjectId(null);
      setSittings([]);
      setIsListFetching(false);
      return;
    }

    setCurrentSittingSubjectId((prevId) => {
      const stillExists = activitiesSubjectsSorted.some((subject) => subject.id === prevId);

      if (stillExists) return prevId;

      return activitiesSubjectsSorted[0].id;
    });
  }, [activitiesSubjectsSorted]);

  React.useEffect(() => {
    if (!currentSittingSubjectId) return;

    let cancelled = false;

    setIsListFetching(true);

    (async () => {
      try {
        const rows = await fetchSittingsForSubject(currentSittingSubjectId);

        if (!cancelled) {
          setSittings(rows);
        }
      } catch (error) {
        console.log(error);

        if (!cancelled) {
          setSittings([]);
        }
      } finally {
        if (!cancelled) {
          setIsListFetching(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentSittingSubjectId]);

  const handleSittingSubject = (subject) => {
    setCurrentSittingSubjectId(subject.id);
  };

  const updateSittings = (sitting) => {
    const subjectId = sitting.subject?.id;

    mutate(
      {
        ...counts,
        activities: Math.max(0, (counts.activities || 0) - 1),
        activitiesData: counts.activitiesData.map((el) => {
          if (el.id !== subjectId) return el;

          if (typeof el.sittingsCount === 'number') {
            return {
              ...el,
              sittingsCount: Math.max(0, el.sittingsCount - 1),
            };
          }

          return {
            ...el,
            sittingsCount: (el.sittingsCount || []).filter((el2) => el2.id !== sitting.id),
          };
        }),
      },
      { revalidate: false }
    );

    setSittings((prev) => prev.filter((el) => el.id !== sitting.id));
  };

  const getSubjectProgram = (subject) => {
    if (!subject) return '';

    if (subject.course) return subject.course.name;
    if (subject.grade) return subject.grade.name;
    if (subject.technical_career) return subject.technical_career.name;

    return '';
  };

  if (!counts || isListFetching) {
    return (
      <Flex justifyContent="center" marginTop="10px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <Box display="flex" fontWeight="bold" fontSize="28px">
        Actividades por calificar{' '}
        <Text color="red" fontWeight="bold">
          ({counts.activities})
        </Text>
      </Box>

      <Flex alignItems="center" maxWidth="300px" marginTop="15px">
        <Menu>
          <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
            <Box display="flex">
              {!!currentSittingSubject ? (
                <>
                  {currentSittingSubject?.name}{' '}
                  <Box as="span" marginLeft="8px" color="red">
                    [{subjectPendingSittingsDisplay(currentSittingSubject)}]
                  </Box>
                </>
              ) : (
                'Seleccione'
              )}
            </Box>
          </MenuButton>

          <MenuList maxHeight="300px" overflowY="scroll">
            {activitiesSubjectsSorted.map((el) => {
              return (
                <MenuItem key={el.id} onClick={() => handleSittingSubject(el)}>
                  <Flex>
                    {el.name}{' '}
                    <Box color="red" marginLeft="8px">
                      [{subjectPendingSittingsDisplay(el)}]
                    </Box>
                  </Flex>
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      </Flex>

      <Table marginTop="sm">
        <Thead>
          <Tr>
            <Th>Estudiante</Th>
            <Th>Código</Th>
            <Th>Materia</Th>
            <Th>Programa</Th>
            <Th>Fecha</Th>
            <Th></Th>
          </Tr>
        </Thead>

        <Tbody>
          {sittings?.map((sitting) => {
            const diffDate = dayjs().diff(sitting.createdAt, 'day');

            return (
              <Tr key={sitting.id} color={diffDate > 1 ? 'red' : undefined}>
                <Td>{sitting.student?.user?.first_name}</Td>
                <Td>{sitting.student?.id}</Td>
                <Td>{sitting.subject?.name}</Td>
                <Td>{getSubjectProgram(sitting.subject)}</Td>
                <Td>{dayjs(sitting?.createdAt).fromNow()}</Td>
                <Td>
                  <Button
                    onClick={() => {
                      setCurrentSitting(sitting);
                      setIsOpen(true);
                    }}
                  >
                    Ver
                  </Button>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      <BasicModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onClose={() => {
          setIsOpen(false);
          setScore('');
        }}
      >
        <ActivityDetail
          score={score}
          setScore={setScore}
          sitting={currentSitting}
          onSave={() => {
            updateSittings(currentSitting);
            setIsOpen(false);
            setScore('');
          }}
          setIsOpen={setIsOpen}
          mutate={mutate}
          counts={counts}
        />
      </BasicModal>
    </Box>
  );
}